import type { MapDocument, TerrainLayer } from "../document/types.ts";
import { FACE_HEIGHT_MAX, faceRows, type TerrainFaceIssue } from "../terrain/faces.ts";
import type { TerrainDefinition, TerrainFace } from "../terrain/types.ts";
import type { ValidationIssue } from "./adapter.ts";

/**
 * A terrain layer's face height in the editor (ADR-072 decision 6): the heights a declared face can be drawn
 * at, the height the editor draws a layer at, the height a terrain change carries, and the Issues entries
 * for a height the editor's context cannot draw. A face here is one the editor can use: the `SessionManager`
 * treats a declaration `validateTerrainFaces` rejects as absent.
 */

/**
 * The heights a face can be drawn at.
 */
export interface FaceHeightRange {
  readonly min: number;
  readonly max: number;
}

/**
 * What the Cliff height field shows: the height the editor draws a layer's face at and the heights it takes.
 */
export interface FaceHeightField extends FaceHeightRange {
  readonly height: number;
}

/**
 * The heights a face can be drawn at: from its own rows to `FACE_HEIGHT_MAX`.
 *
 * @param face The face.
 * @returns The range.
 */
export function faceHeightRange(face: TerrainFace): FaceHeightRange {
  return { min: faceRows(face), max: FACE_HEIGHT_MAX };
}

/**
 * Whether a height can be drawn with a face.
 *
 * @param face The face, or undefined when the terrain declares none the editor can use.
 * @param height The height.
 * @returns True when the face exists and the height is an integer within its range.
 */
export function drawableFaceHeight(face: TerrainFace | undefined, height: number): boolean {
  if (face === undefined) {
    return false;
  }
  const { min, max } = faceHeightRange(face);
  return Number.isInteger(height) && height >= min && height <= max;
}

/**
 * The height the editor draws a terrain layer's face at: the layer's height when its face can draw it,
 * otherwise the face's own rows, as the pack draws it.
 *
 * @param layer The terrain layer.
 * @param face Its terrain's face, or undefined when the terrain declares none the editor can use.
 * @returns The height, 0 without a face.
 */
export function drawnFaceHeight(layer: TerrainLayer, face: TerrainFace | undefined): number {
  if (face === undefined) {
    return 0;
  }
  return layer.faceHeight !== undefined && drawableFaceHeight(face, layer.faceHeight) ? layer.faceHeight : faceRows(face);
}

/**
 * The height a terrain change carries to the new terrain: none when the layer sets none, when the new
 * terrain declares no face, or when its face is taller than the height, and none again when the height is
 * the new face's own rows, so a layer never keeps a height that says nothing.
 *
 * @param height The layer's height, or undefined.
 * @param face The new terrain's face, or undefined.
 * @returns The height the layer keeps, or undefined.
 */
export function carriedFaceHeight(height: number | undefined, face: TerrainFace | undefined): number | undefined {
  if (height === undefined || !drawableFaceHeight(face, height) || height === faceRows(face as TerrainFace)) {
    return undefined;
  }
  return height;
}

/**
 * The Issues entries for faces the editor draws differently from the compile: one per terrain of the
 * context whose declaration is malformed, which the compile refuses, and one per terrain layer of the map
 * whose height its terrain cannot draw (no face the editor can use, or a height outside the face's rows to
 * `FACE_HEIGHT_MAX`), which the editor draws at the pack's height and the compile refuses naming the layer.
 *
 * @param map The map.
 * @param terrains The context's terrains, for their names.
 * @param faceOf A terrain's face the editor can use, or undefined.
 * @param malformed The declarations `validateTerrainFaces` rejects.
 * @returns The issues, errors all, the malformed declarations first.
 */
export function faceHeightIssues(map: MapDocument, terrains: readonly TerrainDefinition[], faceOf: (terrainId: string) => TerrainFace | undefined, malformed: readonly TerrainFaceIssue[]): ValidationIssue[] {
  const nameOf = (id: string): string => terrains.find((t) => t.id === id)?.name ?? id;
  const issues: ValidationIssue[] = malformed.map((problem) => ({
    mapId: map.id,
    kind: "terrain.face-malformed",
    message: `terrain ${nameOf(problem.terrain)} declares a malformed face (${problem.reason}), so the editor draws its layers at the pack's height and the world does not compile; correct the face in the pack table and recompile the world`,
  }));
  for (const layer of map.layers) {
    if (layer.kind !== "terrain" || layer.faceHeight === undefined) {
      continue;
    }
    const face = faceOf(layer.terrain);
    if (face === undefined && malformed.some((problem) => problem.terrain === layer.terrain)) {
      issues.push({ mapId: map.id, kind: "layer.face-height", layerId: layer.id, message: `the cliff height of ${layer.faceHeight} is drawn at the pack's height, since the face terrain ${nameOf(layer.terrain)} declares is malformed; correct the face in the pack table and recompile the world, or change the layer's terrain` });
    } else if (face === undefined) {
      issues.push({ mapId: map.id, kind: "layer.face-height", layerId: layer.id, message: `the cliff height of ${layer.faceHeight} is drawn at the pack's height, since terrain ${nameOf(layer.terrain)} declares no face in the editor's context; recompile the world so its context carries the face, or change the layer's terrain` });
    } else if (!drawableFaceHeight(face, layer.faceHeight)) {
      const { min, max } = faceHeightRange(face);
      issues.push({ mapId: map.id, kind: "layer.face-height", layerId: layer.id, message: `the cliff height of ${layer.faceHeight} is drawn at the pack's height, since terrain ${nameOf(layer.terrain)} draws faces from ${min} to ${max} rows; set a Cliff height from ${min} to ${max}` });
    }
  }
  return issues;
}
